import dayjs from 'dayjs';
import {formatCommaNumber} from '@/utils/utils';
import {ModuSignMethod} from '@/views/ContractManagement/enums';

/**
 * 물품매매V4(MulpumV6) 계약 생성 요청 바디 조립 — 순수 함수로 추출 (SPEC: v3 계약 생성 화면 신설)
 *
 * 왜 있는가:
 * - v1 MulpumV6.vue의 makeContractParam()은 컴포넌트 메서드 내부(this.* 46개 필드 참조)에 있어
 *   단위 테스트가 불가능했다. v3 MulpumV6V3.vue에서 같은 로직을 컴포넌트 밖으로 뽑아
 *   "form state → API 요청 바디" 변환만 담당하는 순수 함수로 만들었다.
 * - 로직 자체는 한 글자도 바꾸지 않았다(필드 순서, trim/replace 체인, 포맷 함수 호출까지 v1과 동일) —
 *   운영 중인 5개 계약서 중 하나이므로 값이 달라지면 조용한 회귀가 된다.
 * - state 인자는 컴포넌트의 data() 필드명을 그대로 반영한 얕은 매핑이다. MulpumV6V3.vue의
 *   createContract()/makeContractDraft()/createTempContract()는 this.* 를 모아 이 함수를 호출한다.
 *
 * 검증: scripts/contracts-v3-payload-check.js 가 _wt-contracts-raw(백엔드) DTO 픽스처를
 * 기준으로 이 함수의 출력을 대조한다.
 */

/**
 * epoch(ms)/dayjs 값을 'YYYY-MM-DD' 문자열로 변환한다. v1 getDateFromEpochTime()과 동일.
 */
export function getDateFromEpochTime(value) {
  if (!value) return '';
  return dayjs(value).format('YYYY-MM-DD');
}

/**
 * MulpumV6 폼 state → POST /contracts/create-by-template (및 temp/embedded-drafts) 요청 바디.
 * v1 MulpumV6.vue의 makeContractParam()을 그대로 이식했다 — 새로 판단하지 않았다.
 */
export function buildMulpumV6Payload(state) {
  const {
    templateId,
    contractType,
    contractTitle,
    contractCreator,
    description,
    clientData,
    itemAmounts,
    promotionCode,
    discountRate,
    selectedPaymentMethod,
    cardCompany,
    selectedPaymentCondition,
    paymentMonths,
    firstPaymentDate,
    staffRows,
    excludableStaffRoles,
    attach2Items,
    confirmInfo,
    contractDate,
    totalPrice,
  } = state;

  const newAddress = `${clientData.address}`.trim();
  const cond = selectedPaymentCondition;
  const months = cond && cond.hasMonths ? String(paymentMonths || '') : '';
  const fmtItem = (v) => (v ? formatCommaNumber(v) : '');

  return {
    templateId,
    contractType,
    contractTitle,
    contractCreator,
    description,
    clientInfos: [
      {
        companyName: clientData.companyName
            .replace('(출고정지)', '')
            .replace('(거래중지_악성미수)', '')
            .replace('(거래중지-영본확인)', '')
            .replace('(거래정지)', ''),
        bizNo: clientData.cNo,
        address: newAddress,
        representative: clientData.ceo.name,
        birthday: getDateFromEpochTime(clientData.ceo.birthday),
        licenseNo: clientData.licenseNo,
        mobile: clientData.contact.mobile,
        email: clientData.contact.email,
        signMethod: ModuSignMethod.SECURE_LINK,
      }
    ],
    itemAmounts: {
      implant: fmtItem(itemAmounts.implant),
      material: fmtItem(itemAmounts.material),
      equipment: fmtItem(itemAmounts.equipment),
      etc: fmtItem(itemAmounts.etc),
    },
    promotionCode,
    discountRate: discountRate.toString(),
    paymentDetails: {
      paymentMethod: selectedPaymentMethod ? selectedPaymentMethod.name : '',
      cardCompany: selectedPaymentMethod && selectedPaymentMethod.name === '신용카드' ? cardCompany : '',
      paymentCondition: cond ? cond.name : '',
      paymentMonths: months,
      firstPaymentDate: getDateFromEpochTime(firstPaymentDate),
    },
    staffSigners: staffRows.map(r => ({
      role: r.role,
      name: r.name,
      mobile: r.mobile,
      deptName: r.deptName,
      excluded: excludableStaffRoles.includes(r.role) ? !!r.excluded : false,
    })),
    attach2Items: attach2Items.map(item => ({
      group: item.group,
      checked: !!item.checked,
      code: item.code || '',
    })),
    confirmInfo: {
      branch: confirmInfo.branch,
      contractDate: getDateFromEpochTime(contractDate),
      installDate: getDateFromEpochTime(confirmInfo.installDate),
    },
    amountNum: parseInt(totalPrice),
    requestMessage: "덴티움 물품매매계약서 송부드립니다. ",
  };
}
